/**
 * Monte Carlo Results Component
 * Displays P10/P50/P90 results from simulation
 */

import React from 'react';
import { FinancialValue } from './shared/FinancialValue';

interface MonteCarloResultsProps {
  result: any;
}

export default function MonteCarloResults({ result }: MonteCarloResultsProps) {
  if (!result) return null;

  const { iterations, p10, p50, p90, mean, std_dev, confidence_interval_95 } = result;

  return (
    <div className="border-2 rounded-lg p-6 space-y-6" style={{ backgroundColor: 'var(--color-bg-surface)', borderColor: 'var(--color-border)' }}>
      <div className="flex items-center justify-between">
        <div>
          <h4 className="text-xl font-bold" style={{ color: 'var(--color-text-primary)' }}>Monte Carlo Simulation Results</h4>
          <p className="text-sm" style={{ color: 'var(--color-needs-human-text)' }}>{iterations?.toLocaleString()} iterations completed</p>
        </div>
        <svg className="w-12 h-12" style={{ color: 'var(--color-brand)' }} fill="currentColor" viewBox="0 0 20 20">
          <path d="M2 11a1 1 0 011-1h2a1 1 0 011 1v5a1 1 0 01-1 1H3a1 1 0 01-1-1v-5zM8 7a1 1 0 011-1h2a1 1 0 011 1v9a1 1 0 01-1 1H9a1 1 0 01-1-1V7zM14 4a1 1 0 011-1h2a1 1 0 011 1v12a1 1 0 01-1 1h-2a1 1 0 01-1-1V4z" />
        </svg>
      </div>

      {/* Percentile Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* P10 */}
        <div className="rounded-lg p-4 border-2 shadow-sm" style={{ backgroundColor: 'var(--color-bg-elevated)', borderColor: 'var(--color-border)' }}>
          <p className="text-sm font-medium mb-1" style={{ color: 'var(--color-text-muted)' }}>P10 (Conservative)</p>
          <p className="text-2xl font-bold" style={{ color: 'var(--color-text-primary)' }}><FinancialValue value={p10} /></p>
          <p className="text-xs mt-1" style={{ color: 'var(--color-text-muted)' }}>10% chance loss is below this</p>
        </div>

        {/* P50 (Median) */}
        <div className="rounded-lg p-4 border-2 shadow-sm" style={{ backgroundColor: 'var(--color-bg-elevated)', borderColor: 'var(--color-needs-human-tint-bd)' }}>
          <p className="text-sm font-medium mb-1" style={{ color: 'var(--color-needs-human-text)' }}>P50 (Median)</p>
          <p className="text-2xl font-bold" style={{ color: 'var(--color-text-primary)' }}><FinancialValue value={p50} /></p>
          <p className="text-xs mt-1" style={{ color: 'var(--color-text-muted)' }}>50th percentile (most likely)</p>
        </div>

        {/* P90 */}
        <div className="rounded-lg p-4 border-2 shadow-lg ring-2" style={{ backgroundColor: 'var(--color-bg-elevated)', borderColor: 'var(--color-brand)', '--tw-ring-color': 'var(--color-brand)' } as React.CSSProperties}>
          <p className="text-sm font-medium mb-1" style={{ color: 'var(--color-needs-human-text)' }}>P90 (Aggressive)</p>
          <p className="text-2xl font-bold" style={{ color: 'var(--color-brand)' }}><FinancialValue value={p90} /></p>
          <p className="text-xs mt-1" style={{ color: 'var(--color-text-muted)' }}>90% chance loss is below this</p>
        </div>
      </div>

      {/* Statistical Summary */}
      <div className="rounded-lg p-4 space-y-3" style={{ backgroundColor: 'var(--color-bg-surface)' }}>
        <h5 className="font-medium" style={{ color: 'var(--color-text-primary)' }}>Statistical Summary</h5>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
          <div>
            <span style={{ color: 'var(--color-text-secondary)' }}>Mean:</span>
            <span className="ml-2 font-semibold"><FinancialValue value={mean} /></span>
          </div>
          <div>
            <span style={{ color: 'var(--color-text-secondary)' }}>Std Dev:</span>
            <span className="ml-2 font-semibold"><FinancialValue value={std_dev} /></span>
          </div>
          <div>
            <span style={{ color: 'var(--color-text-secondary)' }}>95% CI Low:</span>
            <span className="ml-2 font-semibold"><FinancialValue value={confidence_interval_95?.lower} /></span>
          </div>
          <div>
            <span style={{ color: 'var(--color-text-secondary)' }}>95% CI High:</span>
            <span className="ml-2 font-semibold"><FinancialValue value={confidence_interval_95?.upper} /></span>
          </div>
        </div>
      </div>

      {/* Interpretation */}
      <div className="rounded-lg p-4" style={{ backgroundColor: 'var(--color-needs-human-tint)', border: '1px solid var(--color-needs-human-tint-bd)' }}>
        <div className="flex items-start space-x-3">
          <svg className="w-5 h-5 mt-0.5 flex-shrink-0" style={{ color: 'var(--color-brand)' }} fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
          </svg>
          <div className="text-sm" style={{ color: 'var(--color-text-primary)' }}>
            <p className="font-medium mb-1">Interpretation for Settlement Negotiations:</p>
            <p>
              Based on {iterations?.toLocaleString()} simulations with realistic variability,
              there is a <strong>90% probability</strong> that the actual loss will be below <strong><FinancialValue value={p90} /></strong>.
              This P90 figure provides a defensible upper bound for opening demands while maintaining actuarial credibility.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
